// src/admin/AdminPromoManager.jsx
import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase"; // ⚠️ adjust path
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { Megaphone, Image as ImageIcon, ExternalLink, Trash2, Plus } from "lucide-react";

const PROMO_SLOTS = [
  { id: "patient-home-main", label: "Patient Home – Main Banner" },
  { id: "patient-home-secondary", label: "Patient Home – Secondary Banner" },
  { id: "doctor-home-main", label: "Doctor Home – Main Banner" },
];

const PROMO_AUDIENCES = ["all", "patient", "doctor", "clinic"];

const emptyPromo = {
  topLabel: "",
  title: "",
  subtitle: "",
  ctaLabel: "",
  ctaPath: "",
  imageUrl: "",
  audience: "patient",
  audiences: [],
  active: true,
  validFrom: "",
  validTo: "",
  targetType: "internal", // "internal" | "external"
  externalUrl: "",
  priority: 1,
  openInNewTab: true,
  slot: "patient-home-main",
};

export default function AdminPromoManager({ isAdmin }) {
  const [slotId, setSlotId] = useState("patient-home-main");

  // list of promos for current slot
  const [promos, setPromos] = useState([]);
  // currently edited promo
  const [editingId, setEditingId] = useState(null); // Firestore doc id or null for new
  const [editing, setEditing] = useState({ ...emptyPromo, slot: slotId });

  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleChange = (field, value) =>
    setEditing((p) => ({ ...p, [field]: value }));

  const handleAudienceListChange = (value) => {
    const parts = value
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    setEditing((p) => ({ ...p, audiences: parts }));
  };

  const resetToNew = () => {
    setEditingId(null);
    setEditing({ ...emptyPromo, slot: slotId });
  };

  const loadPromos = async (currentSlot) => {
    if (!isAdmin) return;
    setLoadingList(true);
    try {
      const ref = collection(db, "promoSlots");
      const qy = query(
        ref,
        where("slot", "==", currentSlot),
        orderBy("priority", "asc")
      );
      const snap = await getDocs(qy);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPromos(list);
      // if nothing selected, reset form
      if (!editingId) {
        setEditing({ ...emptyPromo, slot: currentSlot });
      }
    } catch (e) {
      console.error("Failed to load promos", e);
      alert("Nuk u ngarkuan promo-t. Provo përsëri.");
    } finally {
      setLoadingList(false);
    }
  };

  const loadSinglePromo = async (promoId) => {
    if (!promoId) return resetToNew();
    try {
      const ref = doc(db, "promoSlots", promoId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        resetToNew();
        return;
      }
      const data = snap.data();
      setEditingId(promoId);
      setEditing({
        topLabel: data.topLabel || "",
        title: data.title || "",
        subtitle: data.subtitle || "",
        ctaLabel: data.ctaLabel || "",
        ctaPath: data.ctaPath || "",
        imageUrl: data.imageUrl || "",
        audience: data.audience || "all",
        audiences: data.audiences || [],
        active: data.active ?? true,
        validFrom: data.validFrom || "",
        validTo: data.validTo || "",
        targetType:
          data.targetType || (data.externalUrl ? "external" : "internal"),
        externalUrl: data.externalUrl || "",
        priority: data.priority ?? 1,
        openInNewTab: data.openInNewTab ?? true,
        slot: data.slot || slotId,
      });
    } catch (e) {
      console.error("Failed to load promo", e);
      alert("Nuk u ngarkua promo. Provo përsëri.");
    }
  };

  const savePromo = async () => {
    if (!editing.title?.trim()) {
      alert("Titulli është i detyrueshëm.");
      return;
    }

    if (editing.targetType === "external") {
      if (!editing.externalUrl?.trim()) {
        alert("Vendos link-un e jashtëm (External URL).");
        return;
      }
    } else {
      if (!editing.ctaPath?.trim()) {
        alert("Vendos rrugën e brendshme (p.sh. /explore).");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        slot: slotId,
        topLabel: editing.topLabel || "",
        title: editing.title || "",
        subtitle: editing.subtitle || "",
        ctaLabel: editing.ctaLabel || "",
        ctaPath:
          editing.targetType === "internal" ? editing.ctaPath || "" : "",
        externalUrl:
          editing.targetType === "external" ? editing.externalUrl || "" : "",
        imageUrl: editing.imageUrl || "",
        audience: editing.audience || "all",
        audiences: editing.audiences || [],
        active: !!editing.active,
        validFrom: editing.validFrom || "",
        validTo: editing.validTo || "",
        targetType: editing.targetType || "internal",
        priority: Number(editing.priority || 1),
        openInNewTab: !!editing.openInNewTab,
      };

      if (editingId) {
        // update existing
        await updateDoc(doc(db, "promoSlots", editingId), payload);
      } else {
        // create new
        const ref = collection(db, "promoSlots");
        const docRef = await addDoc(ref, payload);
        setEditingId(docRef.id);
      }

      await loadPromos(slotId);
      alert("Promo u ruajt ✅");
    } catch (e) {
      console.error("Failed to save promo", e);
      alert("Nuk u ruajt promo. Provo përsëri.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCurrentPromo = async () => {
    if (!editingId) return;
    if (!window.confirm("Je i sigurt që do ta fshish këtë promo?")) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "promoSlots", editingId));
      setEditingId(null);
      setEditing({ ...emptyPromo, slot: slotId });
      await loadPromos(slotId);
      alert("Promo u fshi.");
    } catch (e) {
      console.error("Failed to delete promo", e);
      alert("Nuk u fshi promo. Provo përsëri.");
    } finally {
      setDeleting(false);
    }
  };

  // reload list on slot change
  useEffect(() => {
    if (!isAdmin) return;
    setEditingId(null);
    setEditing({ ...emptyPromo, slot: slotId });
    loadPromos(slotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, slotId]);

  const sortedPromos = useMemo(
    () =>
      [...promos].sort((a, b) => (a.priority || 0) - (b.priority || 0)),
    [promos]
  );

  if (!isAdmin) return null;

  return (
    <div
      style={{
        marginTop: 24,
        padding: 20,
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        background: "white",
        boxShadow: "0 15px 35px rgba(15,23,42,0.08)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "#dbeafe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Megaphone size={18} color="#1d4ed8" />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              Promo Banners (multi-ads)
            </div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Krijo disa ads për të njëjtin slot, me prioritet dhe link-e të
              brendshme ose jashtë MikDok.
            </div>
          </div>
        </div>

        {/* Slot selector */}
        <div style={{ minWidth: 220 }}>
          <Label>Promo slot</Label>
          <select
            value={slotId}
            onChange={(e) => setSlotId(e.target.value)}
            style={selectStyle}
          >
            {PROMO_SLOTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Layout: left list, right form */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 2fr)",
          gap: 20,
          alignItems: "flex-start",
        }}
      >
        {/* Left: list of promos */}
        <div
          style={{
            borderRadius: 14,
            border: "1px solid #e5e7eb",
            padding: 10,
            background: "#f9fafb",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            maxHeight: 420,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              Promos për: {PROMO_SLOTS.find((s) => s.id === slotId)?.label}
            </div>
            <button
              type="button"
              onClick={resetToNew}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                border: "none",
                borderRadius: 999,
                padding: "5px 10px",
                background: "#2563eb",
                color: "white",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <Plus size={14} />
              Promo e re
            </button>
          </div>

          {loadingList ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Duke ngarkuar promos...
            </div>
          ) : sortedPromos.length === 0 ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Asnjë promo për këtë slot. Krijo një të re.
            </div>
          ) : (
            sortedPromos.map((p) => {
              const isSelected = editingId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => loadSinglePromo(p.id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    borderRadius: 10,
                    border: isSelected
                      ? "1px solid #2563eb"
                      : "1px solid #e5e7eb",
                    background: isSelected ? "#eff6ff" : "white",
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {p.title || "Untitled promo"}
                    </div>
                    <span
                      style={{
                        padding: "1px 8px",
                        borderRadius: 999,
                        fontSize: 11,
                        background: p.active ? "#dcfce7" : "#fee2e2",
                        color: p.active ? "#166534" : "#991b1b",
                      }}
                    >
                      {p.active ? "Active" : "Off"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        maxWidth: "70%",
                      }}
                    >
                      {p.subtitle || "-"}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#4b5563",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span># {p.priority ?? 1}</span>
                      {p.targetType === "external" && (
                        <ExternalLink size={12} />
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Right: edit/create form */}
        <div
          style={{
            borderRadius: 16,
            border: "1px solid #e5e7eb",
            padding: 14,
            background: "white",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              {editingId ? "Edit promo" : "Promo e re"}
            </div>
            {editingId && (
              <button
                type="button"
                onClick={deleteCurrentPromo}
                disabled={deleting}
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: "4px 10px",
                  background: "#fee2e2",
                  color: "#b91c1c",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                <Trash2 size={13} />
                {deleting ? "Duke fshirë..." : "Fshi"}
              </button>
            )}
          </div>

          <Field
            label="Top label"
            value={editing.topLabel}
            onChange={(v) => handleChange("topLabel", v)}
            placeholder="p.sh. Ofertë e kufizuar"
          />
          <Field
            label="Titulli"
            required
            value={editing.title}
            onChange={(v) => handleChange("title", v)}
            placeholder="Kontroll kardiologjik me çmim të reduktuar"
          />
          <Field
            label="Përshkrimi"
            value={editing.subtitle}
            onChange={(v) => handleChange("subtitle", v)}
            placeholder="Rezervo online brenda 60 sekondave"
            multiline
          />
          <Field
            label="CTA Label"
            value={editing.ctaLabel}
            onChange={(v) => handleChange("ctaLabel", v)}
            placeholder="Rezervo tani"
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Label>Target</Label>
            <select
              value={editing.targetType}
              onChange={(e) => handleChange("targetType", e.target.value)}
              style={selectStyle}
            >
              <option value="internal">Brenda MikDok (route)</option>
              <option value="external">Link i jashtëm</option>
            </select>
          </div>

          {editing.targetType === "internal" ? (
            <Field
              label="CTA Path (internal)"
              value={editing.ctaPath}
              onChange={(v) => handleChange("ctaPath", v)}
              placeholder="/explore ose /patient/wiki"
            />
          ) : (
            <Field
              label="External URL"
              value={editing.externalUrl}
              onChange={(v) => handleChange("externalUrl", v)}
              placeholder="https://partner-klinike.com/ofertë"
            />
          )}

          <Field
            label="Image URL (opsionale)"
            value={editing.imageUrl}
            onChange={(v) => handleChange("imageUrl", v)}
            placeholder="https://..."
            icon={<ImageIcon size={14} />}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,minmax(0,1fr))",
              gap: 10,
            }}
          >
            <Field
              label="Valid from (ISO date, opsionale)"
              value={editing.validFrom}
              onChange={(v) => handleChange("validFrom", v)}
              placeholder="2025-01-01"
            />
            <Field
              label="Valid to (ISO date, opsionale)"
              value={editing.validTo}
              onChange={(v) => handleChange("validTo", v)}
              placeholder="2025-12-31"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,minmax(0,1fr))",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Label>Audience kryesore</Label>
              <select
                value={editing.audience}
                onChange={(e) => handleChange("audience", e.target.value)}
                style={selectStyle}
              >
                {PROMO_AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label="Priority (1–10, më e vogël = më lart)"
              value={editing.priority}
              onChange={(v) =>
                handleChange("priority", Number(v) || editing.priority || 1)
              }
              type="number"
            />
          </div>

          <Field
            label="Audience list (comma-separated)"
            value={editing.audiences.join(", ")}
            onChange={handleAudienceListChange}
            placeholder="p.sh. patient, doctor"
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: "#111827",
              }}
            >
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => handleChange("active", e.target.checked)}
              />
              Promo është aktive
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: "#111827",
              }}
            >
              <input
                type="checkbox"
                checked={editing.openInNewTab}
                onChange={(e) =>
                  handleChange("openInNewTab", e.target.checked)
                }
              />
              Hap në tab të ri (rekomandohet për link-e jashtë)
            </label>
          </div>

          {/* Preview */}
          <div
            style={{
              marginTop: 4,
              padding: 10,
              borderRadius: 14,
              background:
                "radial-gradient(circle at top left,#eff6ff,white 45%,#f9fafb)",
              border: "1px solid #e5e7eb",
              fontSize: 12,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ fontSize: 11, color: "#6b7280" }}>
              Preview (mobile-ish)
            </div>
            {editing.topLabel && (
              <div
                style={{
                  fontSize: 11,
                  color: "#1d4ed8",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {editing.topLabel}
              </div>
            )}
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {editing.title || "Titulli i promos"}
            </div>
            <div style={{ fontSize: 12, color: "#4b5563" }}>
              {editing.subtitle || "Përshkrimi i shkurtër i promos"}
            </div>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "none",
                  background: "#2563eb",
                  color: "white",
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {editing.ctaLabel || "Shiko ofertën"}
                {editing.targetType === "external" && (
                  <ExternalLink size={11} />
                )}
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={savePromo}
              disabled={saving}
              style={{
                padding: "6px 16px",
                borderRadius: 999,
                border: "none",
                background: "#2563eb",
                color: "white",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {saving ? "Duke ruajtur..." : "Ruaj promo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== UI primitives ========== */

function Label({ children }) {
  return (
    <label
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "#6b7280",
        marginBottom: 2,
      }}
    >
      {children}
    </label>
  );
}

const inputBaseStyle = {
  width: "100%",
  padding: "6px 10px",
  fontSize: 13,
  borderRadius: 10,
  border: "1px solid #d1d5db",
  outline: "none",
};

const selectStyle = {
  ...inputBaseStyle,
  borderRadius: 999,
};

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  multiline,
  icon,
  type = "text",
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Label>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </Label>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            style={{ ...inputBaseStyle, resize: "vertical", paddingRight: 28 }}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            style={{ ...inputBaseStyle, paddingRight: icon ? 26 : 10 }}
          />
        )}
        {icon && (
          <span
            style={{
              position: "absolute",
              right: 8,
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
            }}
          >
            {icon}
          </span>
        )}
      </div>
    </div>
  );
}
